import { canv } from "@/canv/global";
import {
    drawRoundedRect,
    getBoolProperty,
    getNumberProperty,
    getStringProperty,
} from "@/canv/utils";
import { elementBase } from "@/elementBase/elementBase";
import { Group } from "@/group";
import type { CreateSubMenuActions } from "@/widgetMenu/wm_aiAssistant";
import { textWidget } from "../text/text";
import {
    AI_ANSWER_GAP,
    AI_ANSWER_PADDING,
    AI_ICON_HEGHT,
    AI_TITLES,
    LOADING_ANIMATION_HEIGHT,
    LOADING_ANIMATION_PADDING,
    LOADING_ANIMATION_WIDTH,
    PATH_TO_STATIC,
} from "./constants";
import { LoadingAnimation } from "./loadingAnimation";
import type { InteractionTypes, SearchTextObject, WidgetStateMap } from "./types";

export class AiAnswer extends elementBase {
    constructor(startX = 0, startY = 0, action: CreateSubMenuActions = "retro_summary") {
        super();

        this.startX = startX;
        this.startY = startY - AI_ANSWER_GAP - AI_ICON_HEGHT;

        this.textTitle = new textWidget(0, 0);
        this.textTitle.setText(AI_TITLES[action]);
        this.textTitle.menuTextSize(20, true);
        this.textTitle.shapeMode = true;
        this.textTitle.setKeepMinWidth(false);

        this.generatedText = new textWidget(0, 0);
        this.generatedText.shapeMode = true;
        this.generatedText.setKeepMinWidth(false);

        this.aiIcon = new Image();
        this.aiIcon.src = `${canv.host}${PATH_TO_STATIC}widget-icon.svg`;

        this.loadingAnimation = new LoadingAnimation(
            `${canv.host}${PATH_TO_STATIC}loading_dots.json`,
            LOADING_ANIMATION_WIDTH,
            LOADING_ANIMATION_HEIGHT,
        );
        this.loadingAnimation.init();

        this.nothingWhenInteraction = true;

        this.updateDims();
    }

    updateDims() {
        this.textTitle.hardWidth = this.width - 2 * AI_ANSWER_PADDING;
        this.textTitle.Fit();
        this.textTitle.startX = this.startX + AI_ANSWER_PADDING;
        this.textTitle.startY =
            this.startY +
            AI_ICON_HEGHT +
            AI_ANSWER_GAP +
            AI_ANSWER_PADDING +
            this.textTitle.lines[0].textHeight;
        this.textTitle.updateDims();

        this.generatedText.hardWidth = this.width - 2 * AI_ANSWER_PADDING;
        this.generatedText.menuTextSize(14);
        this.generatedText.startX = this.startX + AI_ANSWER_PADDING;
        this.generatedText.startY =
            this.textTitle.startY + AI_ANSWER_GAP + this.generatedText.lines[0].textHeight;
        this.generatedText.updateDims();

        const newShapeHeight =
            2 * AI_ANSWER_PADDING +
            this.textTitle.height +
            AI_ANSWER_GAP +
            this.generatedText.height;
        this.shapeHeight = this.isLoading
            ? Math.max(this.shapeHeight, newShapeHeight)
            : newShapeHeight;

        this.height = this.shapeHeight + AI_ANSWER_GAP + AI_ICON_HEGHT;

        this.updateBase(this.startX, this.startY, this.width, this.height);
    }

    draw() {
        if (this.aiIcon.complete && this.aiIcon.naturalWidth > 0) {
            canv.ctx.drawImage(this.aiIcon, this.startX, this.startY);
        }

        drawRoundedRect(
            this.startX,
            this.startY + this.aiIcon.height + AI_ANSWER_GAP,
            this.width,
            this.shapeHeight,
            16,
        );
        canv.ctx.fillStyle = "#D2ECFF";
        canv.ctx.fill();

        this.textTitle.draw();

        if (this.isLoading && this.loadingAnimation) {
            this.loadingAnimation.draw(
                this.generatedText.startX - LOADING_ANIMATION_PADDING,
                this.textTitle.startY + AI_ANSWER_GAP - LOADING_ANIMATION_PADDING,
            );
        } else {
            this.generatedText.draw();
        }
    }

    async TryAction(type: string) {
        if (this.interactionType === "generatedTextFocus") {
            await this.generatedText.TryAction(type);

            this.updateDims();

            if (type === "keyboardDown") {
                this.tryShortcut();

                return;
            }

            if (type === "mousedown" && !this.generatedText.isOver()) {
                this.stopInteraction();
                canv.toolBox.TryGetToolByName("cursorTool").turnMode("");
                canv.toolBox.TryGetToolByName("cursorTool").TryAction("mousedown");
            }
        }
    }

    tryShortcut() {
        if (canv.keyboard.isEscCommand()) {
            this.stopInteraction();
            canv.toolBox.TryGetToolByName("cursorTool").turnMode("");
        }
    }

    isInteraction(context: string, force = false) {
        if (context === "focus" || (context === "" && force)) {
            if (force || this.generatedText.isOver()) {
                this.fixState("generatedTextFocus");
                this.interactionType = "generatedTextFocus";
                this.generatedText.focusText();
                return { type: "interaction" };
            }
        }

        if (context === "insertText" || context === "pastText") {
            this.fixState("generatedTextFocus");
            this.interactionType = "generatedTextFocus";

            if (canv.keyboard.isEnter()) {
                this.fixState("generatedTextFocus");
                this.interactionType = "generatedTextFocus";

                this.generatedText.focusTextByEnter();
            } else if (!canv.keyboard.isEnter()) {
                this.fixState("generatedTextFocus");
                this.interactionType = "generatedTextFocus";

                this.generatedText.focusText();
                this.generatedText.selection.selectAll();
                this.generatedText.processDelete();

                this.TryAction("keyboardDown");
            }

            this.updateDims();

            return {
                type: "interaction",
            };
        }

        return { type: "none" };
    }

    stopInteraction() {
        if (this.interactionType === "generatedTextFocus") {
            this.generatedText.unfocusText();
            canv.eventManager.openRecord();
            this.saveState("generatedTextFocus");
            canv.eventManager.closeRecord();

            if (!this.generatedText.aiGenerated) {
                this.destroyWidget();
            }
        }

        this.interactionType = "default";
    }

    translate(x: number, y: number) {
        this.startX += x;
        this.startY += y;

        this.updateDims();
    }

    searchText(text: string) {
        const result: SearchTextObject[] = [];
        const textWidget = this.generatedText.getText().toLowerCase();

        if (textWidget.includes(text)) {
            result.push({
                status: true,
                textWidget: this.generatedText,
                mode: "widget",
            });
        }

        return result;
    }

    destroyWidget() {
        const startY =
            this.startY + AI_ICON_HEGHT + AI_ANSWER_GAP + this.generatedText.lines[0].textHeight;
        const newTextWidget = new textWidget(this.startX, startY);
        const group = new Group(true);

        newTextWidget.setParent(group);
        canv.canvasController.addGroup(group);

        const linesFromWidget = [...this.generatedText.lines];

        linesFromWidget.forEach((line) => {
            line.parent = newTextWidget;
        });

        newTextWidget.lines = linesFromWidget;
        newTextWidget.updateDims();

        canv.eventManager.openRecord();
        canv.canvasController.deleteProcessor(this.parent);
        canv.eventManager.createBuildEvent("create", group);
        canv.eventManager.closeRecord();
    }

    getWidgetOptions() {
        const widgetOptions = [];

        return widgetOptions;
    }

    getFixType(reason: string) {
        let fixType = "full";

        switch (reason) {
            case "move":
                fixType = "move";
                break;

            case "resize":
                fixType = "resize";
                break;

            case "bold":
            case "generatedTextFocus":
                fixType = "bold";
                break;

            default:
                return fixType;
        }

        return fixType;
    }

    getState<FixType extends keyof WidgetStateMap>(fixType: FixType): WidgetStateMap[FixType] {
        const state: WidgetStateMap = {
            move: {
                startX: this.startX,
                startY: this.startY,
            },
            resize: {
                startX: this.startX,
                startY: this.startY,
                width: this.width,
            },
            bold: {
                generatedText: this.generatedText.getState("full"),
            },
            full: {
                UID: this.UID,
                parentUID: this.parentUID,
                entityType: "aiAnswer",
                startX: this.startX,
                startY: this.startY,
                width: this.width,
                isLoading: this.isLoading,
                textTitle: this.textTitle.getState("full"),
                generatedText: this.generatedText.getState("full"),
            },
        };

        return state[fixType];
    }

    loadState<FixType extends keyof WidgetStateMap>(
        state: WidgetStateMap[FixType],
        fixType: FixType,
    ) {
        switch (fixType) {
            case "move":
                this.startX = getNumberProperty("startX", state);
                this.startY = getNumberProperty("startY", state);

                break;

            case "resize":
                this.startX = getNumberProperty("startX", state);
                this.startY = getNumberProperty("startY", state);
                this.width = getNumberProperty("width", state);

                break;

            case "bold": {
                const currentState = state as WidgetStateMap["bold"];

                this.generatedText.loadState(currentState);

                break;
            }

            case "full": {
                const currentState = state as WidgetStateMap["full"];

                this.UID = getStringProperty("UID", currentState);
                this.parentUID = getStringProperty("parentUID", currentState);

                this.startX = getNumberProperty("startX", currentState);
                this.startY = getNumberProperty("startY", currentState);
                this.width = getNumberProperty("width", currentState);

                this.isLoading = getBoolProperty("isLoading", currentState);

                this.textTitle.loadState(currentState.textTitle, "full");
                this.generatedText.loadState(currentState.generatedText, "full");

                if (!this.isLoading && this.loadingAnimation) {
                    this.isLoading = false;
                    this.loadingAnimation.destroy();
                    this.loadingAnimation = null;
                }
            }
        }

        this.updateDims();
    }

    constructorName: "aiAnswer" = "aiAnswer";
    interactionType: InteractionTypes = "default";
    startX = 0;
    startY = 0;
    width = 560;
    height = 0;
    shapeHeight = 218;
    isLoading = true;

    textTitle: textWidget;
    generatedText: textWidget;
    aiIcon: HTMLImageElement;
    loadingAnimation: LoadingAnimation | null;
}
